import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Center,
  Divider,
  Heading,
  HStack,
  Image,
  Pressable,
  Spinner,
  Text,
  useDisclose,
  VStack,
} from "native-base";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiEdit, FiMap } from "react-icons/fi";
import * as analytics from "firebase/analytics";
import styles from "./styles.module.css";
import colors from "../../../constants/colors";
import Location from "../../../components/location";
import seat from "../../../assets/icons/seat.svg";
import routes from "../../../routes";
import Cell from "../../../components/cell";
import constants from "../../../constants";
import { StateContext, StatusContext } from "../../../contexts";
import API from "../../../api";
import QuotationFinishedModal from "../../../components/quotationFinishedModal";
import Modal from "../../../components/modal";
import Map from "../../../components/map";
import { Marker, Polyline } from "@react-google-maps/api";
import decodePolyline from "decode-google-map-polyline";
import mapIcons from "../../../constants/mapIcons";
import Vehicles from "../../../components/vehicles";
import RegistrationModal from "../../../components/registrationModal";

function ExperienceCreationDetailsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const state = useContext(StateContext);
  const status = useContext(StatusContext);
  const itineraryContainer = useRef();
  const [origin, setOrigin] = useState({
    ...constants.location,
    type: "origin",
  });
  const [boardingPoints, setBoardingPoints] = useState([]);
  const [intermediateDestinations, setIntermediateDestinations] = useState([]);
  const [destination, setDestination] = useState({
    ...constants.location,
    type: "destination",
  });
  const [returnOrigin, setReturnOrigin] = useState({
    ...constants.location,
    isReturn: true,
    type: "origin",
  });
  const [returnIntermediateDestinations, setReturnIntermediateDestinations] =
    useState([]);
  const [returnBoardingPoints, setReturnBoardingPoints] = useState([]);
  const [returnDestination, setReturnDestination] = useState({
    ...constants.location,
    isReturn: true,
    type: "destination",
  });
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  const [numberOfPassengers, setNumberOfPassengers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const {
    isOpen: isMapModalOpen,
    onOpen: onMapModalOpen,
    onClose: onMapModalClose,
  } = useDisclose();
  const [map, setMap] = useState();
  const [returnMap, setReturnMap] = useState();
  const [itinerary, setItinerary] = useState([]);
  const [polyline, setPolyline] = useState("");
  const [returnPolyline, setReturnPolyline] = useState("");
  const {
    isOpen: isRegistrationModalOpen,
    onOpen: onRegistrationModalOpen,
    onClose: onRegistrationModalClose,
  } = useDisclose();
  const [isLoadingRegistration, setIsLoadingRegistration] = useState(false);

  const getQuotation = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        data: {
          data: {
            itinerary: _itinerary,
            numberOfPassengers: _numberOfPassengers,
            polyline: _polyline,
            returnPolyline: _returnPolyline,
          },
        },
      } = await API(state.jwt).get(`/quotations/${params.id}`);
      const _origin = _itinerary.find(
        (location) => !location.isReturn && location.type === "origin",
      );
      const _boardingPoints = _itinerary.filter(
        (location) => !location.isReturn && location.type === "boardingPoint",
      );
      const _intermediateDestinations = _itinerary.filter(
        (location) =>
          !location.isReturn && location.type === "intermediateDestination",
      );
      const _destination = _itinerary.find(
        (location) => !location.isReturn && location.type === "destination",
      );
      const _isRoundTrip = _itinerary.some((location) => location.isReturn);
      const _returnOrigin = _itinerary.find(
        (location) => location.isReturn && location.type === "origin",
      );
      const _returnIntermediateDestinations = _itinerary.filter(
        (location) =>
          location.isReturn && location.type === "intermediateDestination",
      );
      const _returnBoardingPoints = _itinerary.filter(
        (location) => location.isReturn && location.type === "boardingPoint",
      );
      const _returnDestination = _itinerary.find(
        (location) => location.isReturn && location.type === "destination",
      );
      setItinerary(_itinerary);
      setPolyline(_polyline);
      setReturnPolyline(_returnPolyline);
      setOrigin(_origin);
      setBoardingPoints(_boardingPoints);
      setIntermediateDestinations(_intermediateDestinations);
      setDestination(_destination);
      setIsRoundTrip(_isRoundTrip);
      if (_isRoundTrip) {
        setReturnOrigin(_returnOrigin);
        setReturnIntermediateDestinations(_returnIntermediateDestinations);
        setReturnBoardingPoints(_returnBoardingPoints);
        setReturnDestination(_returnDestination);
      }
      setNumberOfPassengers(_numberOfPassengers);
    } catch (error) {
      if (error?.response?.data?.error?.details?.finished) {
        setFinished(true);
      }
      if (error?.response?.status === 403) {
        navigate(routes.experienceCreation);
      }
      status.updateErrorMessage(error);
    }
    setIsLoading(false);
  }, [navigate, params.id, state.jwt, status]);

  useEffect(() => {
    getQuotation();
  }, [getQuotation]);

  useEffect(() => {
    if (map) {
      const bounds = new window.google.maps.LatLngBounds();
      itinerary
        .filter((location) => !location.isReturn)
        .forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
      map.fitBounds(bounds);
    }
  }, [map, itinerary]);

  useEffect(() => {
    if (isRoundTrip && returnMap) {
      const bounds = new window.google.maps.LatLngBounds();
      itinerary
        .filter((location) => location.isReturn)
        .forEach((location) =>
          bounds.extend(
            new window.google.maps.LatLng(
              location.latitude,
              location.longitude,
            ),
          ),
        );
      returnMap.fitBounds(bounds);
    }
  }, [isRoundTrip, returnMap, itinerary]);

  const onRegister = useCallback(
    async (data) => {
      try {
        setIsLoadingRegistration(true);
        if (data.password !== data.confirmPassword) {
          throw new Error("Contraseñas no coinciden");
        }
        delete data.confirmPassword;
        const {
          data: { user },
        } = await API().post("/auth/local/register", data);
        await API().put(`/quotations/${params.id}`, {
          data: { user: user.id },
        });
        status.updateSuccessMessage(
          "¡Gracias por registrarte! Por favor, verifica tu cuenta a través del enlace que te enviamos por correo electrónico. ¡Bienvenido!",
        );
        onRegistrationModalClose();
        navigate(routes.experienceCreation);
      } catch (error) {
        status.updateErrorMessage(error);
      } finally {
        setIsLoadingRegistration(false);
      }
    },
    [navigate, onRegistrationModalClose, params.id, status],
  );

  const onLogin = useCallback(
    async (data) => {
      try {
        setIsLoadingRegistration(true);
        const {
          data: { jwt, user: _user },
        } = await API().post("/auth/local", data);
        await API().put(`/quotations/${params.id}`, {
          data: {
            agency: _user?.agency?.id || null,
            user: _user.id,
          },
        });
        await state.refreshUser(jwt);
        localStorage.setItem(constants.state.JWT, jwt);
        state.updateJwt(jwt);
        state.updateIsLoggedIn(true);
        onRegistrationModalClose();
      } catch (error) {
        status.updateErrorMessage(error);
      } finally {
        setIsLoadingRegistration(false);
      }
    },
    [onRegistrationModalClose, params.id, state, status],
  );

  return (
    <VStack paddingX={5}>
      <Helmet>
        <title>Confirmación de itinerario</title>
      </Helmet>
      <VStack space={8} paddingBottom={10}>
        <Heading color={colors.secondary} fontSize="2xl">
          Confirmación de itinerario
        </Heading>
        <VStack backgroundColor={colors.white} zIndex={1}>
          <HStack space={5} justifyContent="flex-end" paddingX={8}>
            <Link
              to={routes.experienceCreationId.replace(":id", params.id)}
              disabled={isLoading}>
              <HStack
                backgroundColor={colors.third}
                paddingX={5}
                padding={2}
                borderRadius="md"
                space={3}>
                <FiEdit color={colors.white} size={20} />
                <Text color={colors.white}>Editar</Text>
              </HStack>
            </Link>
          </HStack>
          <VStack ref={itineraryContainer} paddingX={8} paddingY={4}>
            <Text bold color={colors.primary} fontSize="lg">
              Ida
            </Text>
            <Divider backgroundColor={colors.primary} height={1} marginY={3} />
            {isLoading ? (
              <Center height={400}>
                <Spinner size="lg" color={colors.primary} />
              </Center>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <Cell header color={colors.third} />
                    <Cell header color={colors.third} />
                    <Cell header color={colors.third}>
                      Fecha de llegada
                    </Cell>
                    <Cell header color={colors.third}>
                      Hora de llegada
                    </Cell>
                    <Cell header color={colors.third}>
                      Estancia
                    </Cell>
                    <Cell header color={colors.third}>
                      Fecha de salida
                    </Cell>
                    <Cell header color={colors.third}>
                      Hora de salida
                    </Cell>
                    <Cell header color={colors.third}>
                      Observaciones
                    </Cell>
                  </tr>
                </thead>
                <tbody>
                  <Location
                    title="Origen"
                    numeral={1}
                    location={origin}
                    onChange={setOrigin}
                    hideArrivalDate
                    hideStay
                    readOnly
                    readOnlyDepartureDate
                    clearDisabled
                    readOnlyObservation
                  />
                  {boardingPoints.map((boardingPoint, index) => (
                    <Location
                      key={boardingPoint.key}
                      title={`Punto de abordaje #${index + 1}`}
                      numeral={index + 2}
                      location={boardingPoint}
                      readOnly
                      readOnlyDepartureDate
                      clearDisabled
                      readOnlyObservation
                    />
                  ))}
                  {intermediateDestinations.map(
                    (intermediateDestination, index) => (
                      <Location
                        key={intermediateDestination.key}
                        title={`Destino intermedio #${index + 1}`}
                        numeral={boardingPoints.length + index + 2}
                        location={intermediateDestination}
                        readOnly
                        readOnlyDepartureDate
                        clearDisabled
                        readOnlyObservation
                      />
                    ),
                  )}
                  <Location
                    title="Destino final"
                    numeral={
                      boardingPoints.length +
                      intermediateDestinations.length +
                      2
                    }
                    location={destination}
                    onChange={setDestination}
                    isLast
                    hideDepartureDate={!isRoundTrip}
                    hideStay={!isRoundTrip}
                    readOnly
                    readOnlyDepartureDate
                    clearDisabled
                    readOnlyObservation
                  />
                  {isRoundTrip && (
                    <>
                      <tr className={styles.roundTrip}>
                        <Cell bold color={colors.primary} fontSize="xl">
                          Vuelta
                        </Cell>
                        {Array.from(new Array(8)).map((_, index) => (
                          <Cell key={String(index)} />
                        ))}
                      </tr>
                      <Location
                        title="Origen"
                        numeral={1}
                        location={returnOrigin}
                        onChange={setReturnOrigin}
                        hideArrivalDate
                        hideStay
                        disabled
                        readOnlyDepartureDate
                        clearDisabled
                        readOnly
                        readOnlyObservation
                      />
                      {returnIntermediateDestinations.map(
                        (returnIntermediateDestination, index) => (
                          <Location
                            key={returnIntermediateDestination.key}
                            title={`Destino intermedio #${index + 1}`}
                            numeral={index + 2}
                            location={returnIntermediateDestination}
                            readOnly
                            readOnlyDepartureDate
                            clearDisabled
                            readOnlyObservation
                          />
                        ),
                      )}

                      {returnBoardingPoints.map(
                        (returnBoardingPoint, index) => (
                          <Location
                            key={returnBoardingPoint.key}
                            title={`Punto de descenso #${index + 1}`}
                            numeral={returnBoardingPoints.length + index + 2}
                            location={returnBoardingPoint}
                            readOnly
                            readOnlyDepartureDate
                            clearDisabled
                            readOnlyObservation
                          />
                        ),
                      )}
                      <Location
                        title="Final de viaje"
                        numeral={
                          returnBoardingPoints.length +
                          returnIntermediateDestinations.length +
                          2
                        }
                        location={returnDestination}
                        onChange={setReturnDestination}
                        isLast
                        hideDepartureDate
                        hideStay
                        disabled
                        clearDisabled
                        readOnly
                        readOnlyDepartureDate
                        readOnlyObservation
                      />
                    </>
                  )}
                </tbody>
              </table>
            )}
          </VStack>
          <Text bold fontSize="sm" color={colors.gray} paddingLeft={8}>
            * La duración del viaje es estimada.
          </Text>
        </VStack>
        <HStack backgroundColor={colors.white} padding={8} space={50}>
          <HStack space={5}>
            <Heading fontSize="lg" color={colors.third}>
              Tipo de viaje
            </Heading>
            <Text color={colors.gray}>
              {isLoading
                ? "..."
                : isRoundTrip
                ? "Ida y vuelta (viaje redondo)"
                : "Solo ida (viaje sencillo)"}
            </Text>
          </HStack>
          <HStack flex={1} space={5}>
            <Heading fontSize="lg" color={colors.third}>
              Número de pasajeros
            </Heading>
            <Image src={seat} alt="seat" width={7} height={7} />
            <Text color={colors.gray} fontSize="2xl">
              {isLoading ? "..." : numberOfPassengers}
            </Text>
          </HStack>
          <Pressable
            backgroundColor={colors.third}
            paddingX={4}
            paddingY={2}
            borderRadius={5}
            onPress={onMapModalOpen}
            disabled={isLoading}>
            <HStack space={2}>
              <FiMap color={colors.white} size={20} />
              <Text color={colors.white}>Ver en mapa</Text>
            </HStack>
          </Pressable>
          <HStack space={5}>
            <button
              disabled={isLoading}
              onClick={() => {
                if (state.isLoggedIn) {
                  analytics.logEvent(
                    analytics.getAnalytics(),
                    "quotation_confirmed",
                  );
                  navigate(
                    routes.experienceCreationTransportSelection.replace(
                      ":id",
                      params.id,
                    ),
                  );
                } else {
                  onRegistrationModalOpen();
                }
              }}>
              <Box
                backgroundColor={colors.primary}
                paddingY={3}
                paddingX={100}
                borderRadius="full">
                <Text color={colors.white}>Confirmar viaje</Text>
              </Box>
            </button>
          </HStack>
        </HStack>
        <Vehicles />
      </VStack>
      <QuotationFinishedModal isOpen={finished} role="user" />
      <Modal
        maxWidth={900}
        isOpen={isMapModalOpen}
        onRequestClose={onMapModalClose}>
        <VStack padding={5}>
          {isRoundTrip && (
            <Text bold color={colors.primary} fontSize="2xl">
              Ida
            </Text>
          )}
          <Box height={400} width="full">
            <Map setRef={setMap}>
              {Boolean(polyline) && (
                <Polyline
                  path={decodePolyline(polyline)}
                  options={{
                    strokeColor: colors.primary,
                  }}
                />
              )}
              {itinerary
                .filter((location) => !location.isReturn)
                .map((location) => (
                  <Marker
                    key={location.id}
                    position={{
                      lat: location.latitude,
                      lng: location.longitude,
                    }}
                    icon={mapIcons[location.type]}
                    title={location.name}
                  />
                ))}
            </Map>
          </Box>
          {isRoundTrip && (
            <VStack marginTop={5}>
              <Text bold color={colors.primary} fontSize="2xl">
                Vuelta
              </Text>
              <Box height={400} width="full">
                <Map setRef={setReturnMap}>
                  {Boolean(returnPolyline) && (
                    <Polyline
                      path={decodePolyline(returnPolyline)}
                      options={{
                        strokeColor: colors.primary,
                      }}
                    />
                  )}
                  {itinerary
                    .filter((location) => location.isReturn)
                    .map((location) => (
                      <Marker
                        key={location.id}
                        position={{
                          lat: location.latitude,
                          lng: location.longitude,
                        }}
                        icon={mapIcons[location.type]}
                        title={location.name}
                      />
                    ))}
                </Map>
              </Box>
            </VStack>
          )}
        </VStack>
      </Modal>
      <RegistrationModal
        isOpen={isRegistrationModalOpen}
        onRequestClose={onRegistrationModalClose}
        isLoading={isLoadingRegistration}
        onRegister={onRegister}
        onLogin={onLogin}
      />
    </VStack>
  );
}

export default ExperienceCreationDetailsPage;
